import ProgressBar from "../../../components/lib/Progress/ProgressBar";
import { useState, useEffect, useReducer } from "react";
import { Heading } from "../../../components/lib/Text/Heading";
import PageWrapper from "../../../components/lib/Wrapper/PageWrapper";
import Slider from "../../../components/lib/Slider/Slider";
import SliderContent from "../../../components/lib/Slider/SliderContent";
import { encryptObject } from "../../../lib/encrypt";
import { Page } from "@prisma/client";
import useFormAnalytics from "../../../hooks/useFormAnalytics";
import { getAllForms } from "../../api/forms";
import { getFormData } from "../../api/forms/[id]";
import superjson from "superjson";
import { submitForm } from "../../../lib/form";
import { Head } from "next/document";

const FormPage: React.FunctionComponent<any> = ({ formData }) => {
  const { trackFormEvent } = useFormAnalytics();
  const [activePage, setActivePage] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isStarted, setIsStarted] = useState(false);
  const [wasViewed, setWasViewed] = useState(false);
  const [answers, setAnswers] = useReducer(
    (state: any, newState: any) => ({ ...state, ...newState }),
    {}
  );
  const initialScore: { [key: string]: any } = {};
  const [score, setScore] = useState(initialScore);
  const form: any = formData ? superjson.parse(formData) : null;
  useEffect(() => {
    if (form?.id) {
      const index = form.sortedPages.findIndex(
        (id: string) => id === activePage
      );
      setActiveIndex(index);
    }
  }, [activePage]);

  useEffect(() => {
    if (!isStarted && activeIndex > 0) {
      setIsStarted(true);
      trackFormEvent({ id: form.id, event: "start" });
    }

    if (window.parent && window.parent != window) {
      setTimeout(() => {
        const body = document.body;
        const html = document.documentElement;
        const height = Math.max(
          body.scrollHeight,
          body.offsetHeight,
          html.clientHeight,
          html.scrollHeight,
          html.offsetHeight
        );
        const formId = `${form.handle}-${form.shortId}`;
        window.parent.postMessage("setHeight:" + height + ":" + formId, "*");
      }, 400);
    }
  }, [activeIndex]);

  useEffect(() => {
    window.addEventListener("message", (event) => {
      // IMPORTANT: check the origin of the data!
      if (event.origin.startsWith("https://b-fit.info")) {
        // The data was sent from your site.
        // Data sent with postMessage is stored in event.data:
      } else {
        // The data was NOT sent from your site!
        // Be careful! Do not use it. This else branch is
        // here just for clarity, you usually shouldn't need it.
        return;
      }
    });
    if (form?.id) {
      if (!wasViewed) {
        trackFormEvent({ id: form.id, event: "view" });
        setWasViewed(true);
      }
      setActivePage(form.sortedPages[0]);
      setActiveIndex(0);
      let initialState: { [key: string]: any } = {};
      form.sortedPages.forEach((id: string) => {
        initialState[id] = {};
      });
      setAnswers(initialState);
    }
  }, []);
  if (!formData) return null;

  // Create a box wrapper that gets a ref and a currentRef field
  // Whenever the currentRef value changes, and is equal to the ref value
  // Update the box visibility
  // Each wrapper gets a "Next Page Ref" and sets this ref as the current ref when finished
  // Save values and variables (Score) to display on other pages
  const setAnswersAndPublish = async (data: any) => {
    if (!isLoading) {
      const allAnswers = { ...answers, ...data };
      let mustEncryptObject: { [key: string]: any } = {};
      for (const id in allAnswers) {
        if (allAnswers[id].encrypt) {
          mustEncryptObject[id] = allAnswers[id];
        }
      }
      try {
        setIsLoading(true);
        const encryptedData = await encryptObject(mustEncryptObject);
        const allAnswersWithEncryption = { ...allAnswers, ...encryptedData };
        let totalScore: number = 0;
        for (const id in score) {
          totalScore += score[id];
        }
        await submitForm({
          id: form.id,
          answers: allAnswersWithEncryption,
          score: totalScore,
        });
        trackFormEvent({ id: form.id, event: "submit" });
        setActivePage("thank-you");
      } catch (err: any) {
        console.log(err);
      } finally {
        setIsLoading(false);
      }
    }
  };
  if (activePage === "thank-you") {
    return (
      <div className="theme-bfit flex flex-col items-center py-40">
        <div className="max-w-xl text-center">
          <img
            src={form.thankYouPage?.icon ?? "/static/checkmark-large.jpg"}
            width="60px"
            className="mx-auto mb-4"
          />
          <Heading level={2} className="text-center">
            {form.thankYouPage?.heading ??
              "Deine Anfrage wurde erfolgreich verschickt"}
          </Heading>
          {/* <Heading level={6} className="text-center italic mb-4">
            {form.thankYouPage?.subheading ??
              "Nur noch ein Schritt bis du trainieren kannst"}
          </Heading>
           */}
          {form.thankYouPage?.description && (
            <p className="max-w-lg mx-auto mt-4">
              {form.thankYouPage?.description}
            </p>
          )}
        </div>
      </div>
    );
  }
  let currentPage: Page | null = form.pages.find(
    (el: Page) => el.id === activePage
  );
  const wrappers = form.sortedPages?.map((id: String, index: number) => {
    const nextPage =
      index !== form.pages.length - 1 ? form.sortedPages[index + 1] : "";
    const previousPage = index != 0 ? form.sortedPages[index - 1] : "";
    const data = form.pages.find((el: Page) => el.id === id);
    if (data?.id)
      return (
        <PageWrapper
          key={data.id}
          data={data}
          index={index}
          activePage={activePage}
          activeIndex={activeIndex}
          nextPage={nextPage}
          previousPage={previousPage}
          setActivePage={setActivePage}
          setAnswers={setAnswers}
          setAnswersAndPublish={setAnswersAndPublish}
          answers={answers}
          setScore={setScore}
          score={score}
          isLoading={isLoading}
        />
      );
    else return null;
  });
  const navbarPages = form.sortedPages?.map((sortedId: string) => {
    const { id, title, type } = form.pages.find(
      (el: Page) => el.id === sortedId
    );
    return {
      id,
      title,
      type,
    };
  });

  return (
    <div className={`flex flex-col items-center px-2 sm:px-4 lg:px-8`}>
      <ProgressBar
        pages={navbarPages}
        setActivePage={setActivePage}
        active={activePage}
        activeIndex={activeIndex}
      />
      <Heading level={1} className="text-center">
        {currentPage?.heading}
      </Heading>
      <Heading level={6} className="text-center italic mb-8 md:mb-16">
        {currentPage?.description}
      </Heading>
      <Slider>
        <SliderContent>{wrappers}</SliderContent>
      </Slider>
      {!!form.theme?.styles ? (
        <style jsx global>
          {`
            :root {
              ${form.theme.styles}
            }
          `}
        </style>
      ) : null}
    </div>
  );
};

type StaticParams = {
  id: string;
  handle: string;
};
export async function getServerSideProps(ctx: any) {
  const params: StaticParams = ctx.params;
  const form = await getFormData({
    handle: params.handle,
    shortId: params.id,
  });
  try {
    return { props: { formData: superjson.stringify(form) } };
  } catch (err: any) {
    return { props: { formData: null } };
  }
}

export default FormPage;
